import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  Delete,
  FormatAlignLeft,
} from '@mui/icons-material';
import { now } from 'moment';
import toast from 'react-hot-toast';

import { axios } from 'us-web-services';
import PageStyles from '../../styles/PageStyles';
import CronTasks from './CronTasks';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

interface RequestHeader {
  key: string;
  value: string;
  enabled: boolean;
}

interface ApiResponse {
  status: number;
  statusText: string;
  data: unknown;
  headers: Record<string, string>;
  requestHeaders: Record<string, string>;
  time: number;
  size: number;
}

function ApiProfiler() {
  const { classes } = PageStyles();

  const defaultUrl = localStorage.getItem('US_API_PROFILER_URL')
    ? localStorage.getItem('US_API_PROFILER_URL')
    : '/v2/cache-stats';
  const defaultMethod = localStorage.getItem('US_API_PROFILER_METHOD')
    ? localStorage.getItem('US_API_PROFILER_METHOD')
    : 'GET';

  // Profiler state
  const [iterations, setIterations] = useState([]);
  const [counter, setCounter] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [minimumTime, setMinimumTime] = useState(9999999);
  const [maximumTime, setMaximumTime] = useState(0);
  const [averageTime, setAverageTime] = useState(0);
  const [showProfiler, setShowProfiler] = useState(false);

  // Request state
  const [method, setMethod] = useState(defaultMethod);
  const [url, setUrl] = useState(defaultUrl);
  const [requestBody, setRequestBody] = useState('');
  const [showRequestHeaders, setShowRequestHeaders] = useState(false);

  const getInitialHeaders = (): RequestHeader[] => {
    const token = localStorage.getItem('US_USER_AUTH_TOKEN');
    const baseHeaders = [
      { key: 'Accept', value: 'application/json', enabled: true },
      { key: 'Content-Type', value: 'application/json', enabled: true },
    ];

    if (token) {
      baseHeaders.push({ key: 'Authorization', value: `Bearer ${token}`, enabled: true });
    }

    return baseHeaders;
  };

  const [headers, setHeaders] = useState<RequestHeader[]>(getInitialHeaders());

  // Response state
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [showResponseHeaders, setShowResponseHeaders] = useState<boolean | null>(false);
  const [responseError, setResponseError] = useState<string | null>(null);

  const updateTimes = (time, avg) => {
    if (time < minimumTime) {
      setMinimumTime(time);
    }

    if (time > maximumTime) {
      setMaximumTime(time);
    }

    setAverageTime(avg);
  };

  const updateMethod = event => {
    localStorage.setItem('US_API_PROFILER_METHOD', event.target.value);
    setMethod(event.target.value);
  };

  const updateUrl = event => {
    localStorage.setItem('US_API_PROFILER_URL', event.target.value);
    setUrl(event.target.value);
  };

  const updateRequestBody = event => {
    setRequestBody(event.target.value);
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(requestBody);

      setRequestBody(JSON.stringify(parsed, null, 2));
    } catch {
      toast.error('Invalid JSON');
    }
  };

  const getRequestHeaders = (): Record<string, string> => {
    const headerObj: Record<string, string> = {};

    headers.filter(h => h.enabled && h.key).forEach(h => {
      headerObj[h.key] = h.value;
    });

    return headerObj;
  };

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '', enabled: true }]);
  };

  const updateHeader = (index: number, field: keyof RequestHeader, value: string | boolean) => {
    const newHeaders = [...headers];

    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setHeaders(newHeaders);
  };

  const removeHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusColor = (status: number): 'success' | 'error' | 'warning' => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400) return 'error';

    return 'warning';
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  async function makeCall(): Promise<ApiResponse> {
    const start = now();

    let parsedBody;

    if (requestBody) {
      try {
        parsedBody = JSON.parse(requestBody);
      } catch {
        throw new Error('Invalid JSON in request body. Please check the syntax.');
      }
    }

    try {
      const result = await axios({
        method,
        url,
        data: parsedBody,
        headers: getRequestHeaders(),
      });
      const end = now();
      const responseText = JSON.stringify(result.data);

      return {
        status: result.status,
        statusText: result.statusText,
        data: result.data,
        headers: result.headers as Record<string, string>,
        requestHeaders: result.config.headers as Record<string, string>,
        time: end - start,
        size: new Blob([responseText]).size,
      };
    } catch (error: any) {
      const end = now();

      if (error.response) {
        const responseText = JSON.stringify(error.response.data);

        return {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers as Record<string, string>,
          requestHeaders: error.config?.headers as Record<string, string> || {},
          time: end - start,
          size: new Blob([responseText]).size,
        };
      }
      throw error;
    }
  }

  async function callApi() {
    setIsLoading(true);
    setResponseError(null);

    try {
      const start = now();
      const apiResponse = await makeCall();
      const end = now();
      const time = end - start;

      setResponse(apiResponse);

      if (showProfiler) {
        const tempCounter = counter + 1;
        const tempTotalTime = totalTime + time;

        setCounter(tempCounter);
        setTotalTime(tempTotalTime);
        const avg = tempTotalTime / tempCounter;

        const tempIterations = [...iterations];

        tempIterations.push({
          id: tempCounter,
          start,
          end,
          time,
          totalTime: tempTotalTime,
          avg,
        });
        setIterations(tempIterations);
        updateTimes(time, avg);
      }
    } catch (error: any) {
      // Extract API error message from response if available
      let errorMessage = 'Request failed';

      if (error.response?.data?.errors?.[0]?.message) {
        errorMessage = error.response.data.errors[0].message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setResponseError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  const clearAll = () => {
    setIterations([]);
    setCounter(0);
    setTotalTime(0);
    setMinimumTime(9999999);
    setMaximumTime(0);
    setAverageTime(0);
    setResponse(null);
    setResponseError(null);
  };

  return (
    <>
      <Paper className={classes.paper}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant='h6'>API Tester</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='body2' color='text.secondary'>Profiler Mode</Typography>
            <Switch
              checked={showProfiler}
              onChange={e => setShowProfiler(e.target.checked)}
              size='small'
            />
          </Box>
        </Box>

        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size='small'>
              <InputLabel>Method</InputLabel>
              <Select
                value={method}
                label='Method'
                onChange={updateMethod}
              >
                {HTTP_METHODS.map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              required
              id='url'
              name='url'
              label='URL'
              fullWidth
              size='small'
              value={url}
              onChange={updateUrl}
              placeholder='/v2/endpoint'
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant='contained'
              color='primary'
              onClick={callApi}
              disabled={isLoading}
              fullWidth
              startIcon={isLoading ? <CircularProgress size={16} color='inherit' /> : null}
            >
              {isLoading ? 'Sending...' : 'Send'}
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Tabs
            value={showRequestHeaders ? 1 : 0}
            onChange={(_, v) => setShowRequestHeaders(v === 1)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label='Body' />
            <Tab label={`Headers (${headers.filter(h => h.enabled).length})`} />
          </Tabs>

          {!showRequestHeaders && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Tooltip title='Format JSON'>
                  <IconButton size='small' onClick={formatJson}>
                    <FormatAlignLeft fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                id='requestBody'
                name='requestBody'
                placeholder='{ "key": "value" }'
                fullWidth
                multiline
                minRows={6}
                maxRows={12}
                value={requestBody}
                onChange={updateRequestBody}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                }}
              />
            </Box>
          )}

          {showRequestHeaders && (
            <Box sx={{ mt: 2 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox' />
                    <TableCell>Key</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell padding='checkbox' />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((header, index) => (
                    <TableRow key={index}>
                      <TableCell padding='checkbox'>
                        <Switch
                          size='small'
                          checked={header.enabled}
                          onChange={e => updateHeader(index, 'enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          value={header.key}
                          onChange={e => updateHeader(index, 'key', e.target.value)}
                          placeholder='Header name'
                          variant='standard'
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          value={header.value}
                          onChange={e => updateHeader(index, 'value', e.target.value)}
                          placeholder='Header value'
                          variant='standard'
                        />
                      </TableCell>
                      <TableCell padding='checkbox'>
                        <IconButton size='small' onClick={() => removeHeader(index)}>
                          <Delete fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button
                size='small'
                startIcon={<Add />}
                onClick={addHeader}
                sx={{ mt: 1 }}
              >
                Add Header
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
          <CronTasks
            setUrl={setUrl}
            setMethod={setMethod}
          />
          <Button
            variant='outlined'
            onClick={clearAll}
            size='small'
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {(response || responseError) && (
        <Card className={classes.paper}>
          <CardContent>
            {responseError ? (
              <Typography color='error'>{responseError}</Typography>
            ) : response && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip
                    label={`${response.status} ${response.statusText}`}
                    color={getStatusColor(response.status)}
                    size='small'
                  />
                  <Typography variant='body2' color='text.secondary'>
                    {response.time} ms
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {formatBytes(response.size)}
                  </Typography>
                </Box>

                <Tabs
                  value={showResponseHeaders ? 2 : showResponseHeaders === false ? 0 : 1}
                  onChange={(_, v) => setShowResponseHeaders(v === 2 ? true : v === 1 ? null : false)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label='Body' />
                  <Tab label='Request Headers' />
                  <Tab label='Response Headers' />
                </Tabs>

                {showResponseHeaders === false && (
                  <Box sx={{ mt: 2, position: 'relative' }}>
                    <Tooltip title='Copy response'>
                      <IconButton
                        size='small'
                        onClick={() => copyToClipboard(JSON.stringify(response.data, null, 2))}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                      >
                        <ContentCopy fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Box
                      component='pre'
                      sx={{
                        bgcolor: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        maxHeight: 400,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(response.data, null, 2)}
                    </Box>
                  </Box>
                )}

                {showResponseHeaders === null && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant='subtitle2' gutterBottom>
                      Actual Headers Sent (including auto-injected auth)
                    </Typography>
                    <Table size='small'>
                      <TableBody>
                        {Object.entries(response.requestHeaders)
                          .filter(([key]) => key && typeof key === 'string')
                          .map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                              <TableCell sx={{ wordBreak: 'break-all' }}>{String(value)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {showResponseHeaders === true && (
                  <Box sx={{ mt: 2 }}>
                    <Table size='small'>
                      <TableBody>
                        {Object.entries(response.headers).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                            <TableCell>{value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Collapse in={showProfiler}>
        <Card className={classes.paper}>
          <CardContent>
            <Typography variant='subtitle2' gutterBottom>Profiler Stats</Typography>
            <Table size='small'>
              <TableBody>
                <TableRow>
                  <TableCell>Min</TableCell>
                  <TableCell>{minimumTime === 9999999 ? '-' : `${minimumTime} ms`}</TableCell>
                  <TableCell>Max</TableCell>
                  <TableCell>{maximumTime === 0 ? '-' : `${maximumTime} ms`}</TableCell>
                  <TableCell>Avg</TableCell>
                  <TableCell>{averageTime === 0 ? '-' : `${averageTime.toFixed(1)} ms`}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
          {iterations.length > 0 && (
            <CardContent>
              <Typography variant='subtitle2' gutterBottom>Iterations</Typography>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Avg</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {iterations.map(iteration => (
                    <TableRow key={iteration.id}>
                      <TableCell>{iteration.id}</TableCell>
                      <TableCell>{iteration.time} ms</TableCell>
                      <TableCell>{iteration.totalTime} ms</TableCell>
                      <TableCell>{iteration.avg.toFixed(1)} ms</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          )}
        </Card>
      </Collapse>
    </>
  );
}

export default withRouter(ApiProfiler);
